import React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';

type RejectUrlDialogProps = {
  open: boolean;
  merchantName: string;
  url: string;
  reason: string;
  onReasonChange: (reason: string) => void;
  loading?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
};

const RejectUrlDialog: React.FC<RejectUrlDialogProps> = ({
  open,
  merchantName,
  url,
  reason,
  onReasonChange,
  loading = false,
  error = null,
  onConfirm,
  onCancel,
}) => {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !loading && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
            Reject URL
          </DialogTitle>
          <DialogDescription>
            Rejecting <strong>{url}</strong> for <strong>{merchantName}</strong>. Payments from this
            URL will remain blocked. The merchant will see this reason on their submission.
          </DialogDescription>
        </DialogHeader>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="text-muted-foreground">Reason (optional)</span>
          <Textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="e.g. Domain ownership could not be verified"
            rows={3}
          />
        </label>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RejectUrlDialog;
